import { useState, useEffect } from "react";
import axios from "axios";
import { Check, RotateCcw, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Inline price editor for a single menu item. Shows the current effective price
 * (either the DB override or the hard-coded default) and lets the admin update
 * or reset it. Auto-formats to two decimals and clamps to 0-999.
 */
export const PriceEditor = ({ itemId, currentPrice, adminKey, onChanged }) => {
  const [value, setValue] = useState(String(currentPrice ?? ""));
  const [saving, setSaving] = useState(false);
  const [flash, setFlash] = useState(null); // "saved" | "reset" | null

  useEffect(() => setValue(String(currentPrice ?? "")), [currentPrice]);

  const parsed = parseFloat(value.replace(",", "."));
  const changed = !Number.isNaN(parsed) && Math.abs(parsed - Number(currentPrice)) > 0.001;

  const save = async () => {
    if (Number.isNaN(parsed) || parsed < 0 || parsed > 999) return;
    setSaving(true);
    try {
      await axios.post(`${API}/admin/menu/${itemId}/price`, null, {
        params: { key: adminKey, price: parsed },
      });
      setFlash("saved");
      setTimeout(() => setFlash(null), 1400);
      onChanged && onChanged();
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    setSaving(true);
    try {
      await axios.delete(`${API}/admin/menu/${itemId}/price`, {
        params: { key: adminKey },
      });
      setFlash("reset");
      setTimeout(() => setFlash(null), 1400);
      onChanged && onChanged();
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid={`price-editor-${itemId}`}
      className="flex items-center gap-1.5 bg-[#141414] border border-q5 px-2 py-1.5"
    >
      <span className="text-[10px] uppercase tracking-[0.15em] text-neutral-500 flex-shrink-0">€</span>
      <input
        type="text"
        inputMode="decimal"
        data-testid={`price-input-${itemId}`}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && changed && save()}
        className="flex-1 min-w-0 bg-transparent text-white text-sm font-medium focus:outline-none focus:text-gold"
      />
      {saving ? (
        <Loader2 size={13} className="text-gold animate-spin flex-shrink-0" />
      ) : flash === "saved" ? (
        <Check size={13} className="text-emerald-400 flex-shrink-0" />
      ) : flash === "reset" ? (
        <RotateCcw size={13} className="text-neutral-400 flex-shrink-0" />
      ) : changed ? (
        <button
          type="button"
          data-testid={`price-save-${itemId}`}
          onClick={save}
          aria-label="Guardar precio"
          className="w-6 h-6 bg-gold/20 hover:bg-gold/40 text-gold flex items-center justify-center transition-colors flex-shrink-0"
        >
          <Check size={12} />
        </button>
      ) : (
        <button
          type="button"
          data-testid={`price-reset-${itemId}`}
          onClick={reset}
          aria-label="Restablecer precio"
          title="Restablecer al precio original"
          className="w-6 h-6 border border-q5 text-neutral-500 hover:text-neutral-300 hover:border-neutral-500 flex items-center justify-center transition-colors flex-shrink-0"
        >
          <RotateCcw size={11} />
        </button>
      )}
    </div>
  );
};
